import { useCallback, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import styles from './LightboxPreview.module.css';

export interface ScanItem {
  scan_id: string;
  thumbnail_url: string;
  /** ≤2048px "social" image shown full-screen in the lightbox. */
  preview_url?: string;
  original_filename: string;
  /** Present only on customer-created edit scans. */
  source_scan_id?: string;
}

export interface LightboxPreviewProps {
  scans: ScanItem[];
  initialIndex: number;
  onClose: () => void;
  /**
   * When provided, an "Edit" button is shown for scans that are not
   * themselves edits (edits always start from a lab master). The gallery
   * passes this only when the scan-editing flag is on and not in admin
   * preview.
   */
  onEdit?: (scan: ScanItem) => void;
}

export function LightboxPreview({ scans, initialIndex, onClose, onEdit }: LightboxPreviewProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === scans.length - 1;
  const currentScan = scans[currentIndex];

  // "Blur-up": the grid thumbnail (already cached) is shown blurred instantly
  // while the full-resolution preview downloads, then the preview fades in over
  // it. `loadedScanId` tracks which scan's preview has finished loading, derived
  // per-render so navigation shows the placeholder again with no flash.
  const [loadedScanId, setLoadedScanId] = useState<string | null>(null);
  const currentLoaded = loadedScanId === currentScan.scan_id;
  const markCurrentLoaded = useCallback(
    () => setLoadedScanId(currentScan.scan_id),
    [currentScan.scan_id],
  );

  const goToPrevious = useCallback(() => {
    if (!isFirst) {
      setCurrentIndex((i) => i - 1);
    }
  }, [isFirst]);

  const goToNext = useCallback(() => {
    if (!isLast) {
      setCurrentIndex((i) => i + 1);
    }
  }, [isLast]);

  // Keyboard support: Escape to close, ArrowLeft/ArrowRight for navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goToPrevious, goToNext]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Swipe gesture handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: goToNext,
    onSwipedRight: goToPrevious,
    preventScrollOnSwipe: true,
    trackMouse: false,
  });

  // The grid uses the small thumbnail; the lightbox shows the ≤2048px social
  // "preview" image, which arrives on the same gallery payload. Fall back to
  // the thumbnail if a payload predates preview_url.
  const previewSrc = currentScan.preview_url ?? currentScan.thumbnail_url;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Scan preview">
      <button
        className={styles.closeButton}
        onClick={onClose}
        aria-label="Close preview"
        type="button"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>

      <div className={styles.content} {...swipeHandlers}>
        <button
          className={`${styles.navButton} ${styles.prevButton}`}
          onClick={goToPrevious}
          aria-label="Previous scan"
          aria-disabled={isFirst}
          type="button"
        >
          <svg width="12" height="20" viewBox="0 0 12 20" fill="none" aria-hidden="true">
            <path d="M10 2L2 10L10 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className={styles.imageContainer}>
          {/* Blurred, already-cached grid thumbnail — shown instantly, then
              faded out as the full preview loads on top. Decorative. */}
          <img
            className={`${styles.placeholder} ${currentLoaded ? styles.placeholderHidden : ''}`}
            src={currentScan.thumbnail_url}
            alt=""
            aria-hidden="true"
            draggable={false}
          />
          <img
            key={currentScan.scan_id}
            className={`${styles.image} ${currentLoaded ? styles.imageVisible : ''}`}
            src={previewSrc}
            alt={currentScan.original_filename}
            ref={(node) => {
              // Cached images can finish before onLoad is attached; catch that.
              if (node && node.complete && node.naturalWidth > 0) markCurrentLoaded();
            }}
            onLoad={markCurrentLoaded}
            draggable={false}
          />
        </div>

        <button
          className={`${styles.navButton} ${styles.nextButton}`}
          onClick={goToNext}
          aria-label="Next scan"
          aria-disabled={isLast}
          type="button"
        >
          <svg width="12" height="20" viewBox="0 0 12 20" fill="none" aria-hidden="true">
            <path d="M2 2L10 10L2 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {onEdit && !currentScan.source_scan_id && (
        <button
          type="button"
          className={styles.editButton}
          onClick={() => onEdit(currentScan)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M11.5 1.5l3 3L5.5 13.5 2 14l0.5-3.5 9-9z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
          Edit photo
        </button>
      )}

      <span className={styles.positionIndicator}>
        {currentIndex + 1} / {scans.length}
      </span>
    </div>
  );
}
